/**
 * SB6-II-01 — mutation hooks for the inspection-instructions feature.
 *
 * PATCH (inline + modal edits), POST create, POST /transition
 * (WITHDRAW/REJECT), POST /bulk-assign. The inline cells own their own
 * optimistic-update + rollback locally (mirroring the Data Acquisitions
 * `AssigneeChip`); these hooks are the thin mutationFn + list-invalidation
 * layer so the optimistic logic stays close to the cell.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { projectsFetch } from "../projects/projectsFetch.js";
import { iiKeys } from "./keys.js";
import type {
  BulkAssignBody,
  CreateInspectionInstructionBody,
  IiListItem,
  PatchInspectionInstructionBody,
  TransitionInspectionInstructionBody,
} from "./types.js";

const BASE = "/inspection-instructions";

export function usePatchInspectionInstructionMutation() {
  const qc = useQueryClient();
  return useMutation<
    IiListItem,
    Error,
    { id: string; body: PatchInspectionInstructionBody }
  >({
    mutationFn: ({ id, body }) =>
      projectsFetch<IiListItem>(`${BASE}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: iiKeys.lists() });
    },
  });
}

export function useCreateInspectionInstructionMutation() {
  const qc = useQueryClient();
  return useMutation<IiListItem, Error, CreateInspectionInstructionBody>({
    mutationFn: (body) =>
      projectsFetch<IiListItem>(BASE, { method: "POST", body }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: iiKeys.lists() });
    },
  });
}

export function useTransitionInspectionInstructionMutation() {
  const qc = useQueryClient();
  return useMutation<
    IiListItem,
    Error,
    { id: string; body: TransitionInspectionInstructionBody }
  >({
    mutationFn: ({ id, body }) =>
      projectsFetch<IiListItem>(
        `${BASE}/${encodeURIComponent(id)}/transition`,
        { method: "POST", body },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: iiKeys.lists() });
    },
  });
}

export function useBulkAssignMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, Error, BulkAssignBody>({
    mutationFn: (body) =>
      projectsFetch<undefined>(`${BASE}/bulk-assign`, {
        method: "POST",
        body,
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: iiKeys.lists() });
    },
  });
}
